import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUpRight, Check, FileText, Loader2, Lock, MousePointerClick, Package, Paperclip, PenTool, Send, X } from "lucide-react";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import api, { resolveMedia } from "@/lib/api";
import { FocalImage } from "@/components/FocalImage";
import { useSettings } from "@/context/SettingsContext";
import { getStudioKey } from "@/lib/studio";
import useSEO from "@/lib/useSEO";
import { Reveal } from "@/components/motion/Reveal";

const eur = (n) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(n) || 0);

const STEPS_INFO = [
  { n: "01", icon: PenTool, title: "Raccontaci il tuo ambiente", text: "Stile, esigenze, misure e budget: il punto di partenza è la tua casa reale, non un catalogo." },
  { n: "02", icon: Package, title: "Lo Studio seleziona", text: "Arredi, illuminazione e complementi coerenti tra loro, scelti con criterio progettuale e non per caso." },
  { n: "03", icon: FileText, title: "Ricevi la Shopping List", text: "Immagini, caratteristiche, finiture, quantità, prezzi e tempi indicativi di consegna, in un documento elegante." },
  { n: "04", icon: MousePointerClick, title: "Acquista direttamente", text: "Ogni prodotto dispone di link diretto allo Shop: dalla selezione all'ordine in pochi click." },
];

const ENVIRONMENTS = ["Living", "Sala da pranzo", "Cucina", "Camera matrimoniale", "Cameretta", "Studio", "Ingresso", "Bagno", "Outdoor", "Altro"];
const BUDGETS = ["Fino a €3.000", "€3.000 – €5.000", "€5.000 – €10.000", "€10.000 – €20.000", "Oltre €20.000", "Da definire"];
const TIMELINES = ["Il prima possibile", "Entro 1–3 mesi", "Entro 3–6 mesi", "Sto ancora valutando"];

const WIZARD_STEPS = ["Superficie", "Ambienti", "Budget", "Tempistica", "Brief", "I tuoi dati", "Riepilogo"];

const COMPRENDE = [
  "Selezione professionale e coordinata dei prodotti",
  "Arredi, illuminazione e complementi",
  "Indicazione delle finiture e varianti selezionate",
  "Shopping List personalizzata",
  "Immagini e caratteristiche dei prodotti",
  "Prezzi",
  "Tempi indicativi di consegna",
  "Link diretti all'acquisto",
  "Condizioni esclusive Cliente Studio sui prodotti idonei dello Shop",
  "Una revisione della selezione",
];

const NON_COMPRENDE = [
  "Progettazione distributiva",
  "Modifica della disposizione degli ambienti",
  "Planimetrie progettuali",
  "Render fotorealistici",
  "Disegni tecnici / esecutivi",
  "Progettazione impiantistica",
  "Progettazione illuminotecnica tecnica",
  "Progettazione di arredi su misura",
  "Assistenza o direzione lavori",
];

export default function ShopTheProject() {
  useSEO({
    title: "Shop the Project — Dal progetto agli acquisti",
    description: "Il servizio di selezione arredi di Giovanna Matino Interior: lo Studio sceglie arredi, illuminazione e complementi coordinati e te li consegna in una Shopping List pronta da acquistare.",
  });
  const { settings } = useSettings();
  const navigate = useNavigate();
  const TIERS = [
    { key: "t25", label: "Fino a 25 m²", price: Number(settings.stp_price_25 ?? 149) },
    { key: "t50", label: "Fino a 50 m²", price: Number(settings.stp_price_50 ?? 249) },
    { key: "t70", label: "Fino a 70 m²", price: Number(settings.stp_price_70 ?? 349) },
    { key: "t110", label: "Fino a 110 m²", price: Number(settings.stp_price_110 ?? 449) },
    { key: "xl", label: "Oltre 110 m²", price: null },
  ];
  const percent = Number(settings.studio_discount_percent ?? 10);

  // ---------- Accesso personale ----------
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessLink, setAccessLink] = useState("");
  const [accessBusy, setAccessBusy] = useState(false);
  const [accessError, setAccessError] = useState("");

  // ---------- Configuratore ----------
  const [step, setStep] = useState(0);
  const [tier, setTier] = useState("");
  const [environments, setEnvironments] = useState([]);
  const [budget, setBudget] = useState("");
  const [timeline, setTimeline] = useState("");
  const [brief, setBrief] = useState({ style: "", needs: "", colors: "", keep_existing: "", notes: "" });
  const [attachments, setAttachments] = useState([]); // [{url, name}]
  const [customer, setCustomer] = useState({ name: "", surname: "", email: "", phone: "" });
  const [privacy, setPrivacy] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [website, setWebsite] = useState("");
  const [method, setMethod] = useState("stripe");
  const [gcCode, setGcCode] = useState("");
  const [gc, setGc] = useState(null); // Gift Card validata dal server {balance}
  const [gcBusy, setGcBusy] = useState(false);
  // Chiave idempotenza: rigenerata solo se cambia la fascia di prezzo.
  // Doppio click / retry riutilizzano la stessa sessione di pagamento lato server.
  const [idemKey, setIdemKey] = useState(() => crypto.randomUUID());
  useEffect(() => { setIdemKey(crypto.randomUUID()); }, [tier]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [done, setDone] = useState(null); // {quotation} | {order_number}
  const [paypalCfg, setPaypalCfg] = useState(null);
  const formRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/payments/paypal/config").then((r) => setPaypalCfg(r.data)).catch(() => setPaypalCfg({ enabled: false }));
  }, []);

  // Apre il pannello accesso se arrivo con #accesso (da menu mobile "Area Cliente")
  useEffect(() => {
    if (window.location.hash === "#accesso") openAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openAccess = () => {
    const k = getStudioKey();
    if (k) { navigate(`/progetto/${k}`); return; }
    setAccessError(""); setAccessLink(""); setAccessOpen(true);
  };

  const submitAccess = async (e) => {
    e.preventDefault();
    setAccessError("");
    const m = accessLink.match(/([a-f0-9]{32,128})/i);
    if (!m) { setAccessError("Incolla il link personale che hai ricevuto dallo Studio (lo trovi nell'email o nella Shopping List)."); return; }
    setAccessBusy(true);
    try {
      await api.get(`/studio/${m[1]}/pricing`);
      localStorage.setItem("gm_studio_key", m[1]);
      navigate(`/progetto/${m[1]}`);
    } catch (err) {
      const status = err.response?.status;
      setAccessError(status === 410
        ? "Questo accesso è scaduto o è stato revocato. Contatta lo Studio per rinnovarlo."
        : "Link non valido. Controlla di aver copiato tutto il link ricevuto dallo Studio.");
    } finally { setAccessBusy(false); }
  };

  const scrollToForm = () => document.getElementById("richiedi")?.scrollIntoView({ behavior: "smooth" });
  const selTier = TIERS.find((t) => t.key === tier);
  const isQuote = tier === "xl";

  const canNext = () => {
    if (step === 0) return !!tier;
    if (step === 1) return environments.length > 0;
    if (step === 2) return !!budget;
    if (step === 3) return !!timeline;
    if (step === 4) return true;
    if (step === 5) return customer.name.trim() && /\S+@\S+\.\S+/.test(customer.email) && privacy;
    return true;
  };

  const toggleEnv = (v) => setEnvironments((p) => (p.includes(v) ? p.filter((x) => x !== v) : [...p, v]));

  const uploadFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (attachments.length >= 5) { toast.error("Massimo 5 allegati"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/shop-the-project/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAttachments((p) => [...p, { url: data.url, name: f.name }]);
      toast.success("File caricato");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload non riuscito"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const buildPayload = () => ({
    surface_tier: tier, environments, budget, timeline, brief,
    attachments: attachments.map((a) => a.url),
    customer, privacy_consent: privacy, marketing_consent: marketing, website, method,
    idempotency_key: idemKey,
    giftcard_code: gc ? gcCode.trim().toUpperCase() : "",
  });

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/shop-the-project/order", buildPayload());
      if (data.quotation) { setDone({ quotation: true }); return; }
      if (data.free && data.checkout_url) { window.location.href = data.checkout_url; return; } // coperto dalla Gift Card
      if (method === "stripe") { window.location.href = data.checkout_url; return; }
      return data; // paypal: gestito da startPaypal
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'invio. Riprova.");
      throw err;
    } finally { setBusy(false); }
  };

  const startPaypal = async () => {
    const { data } = await api.post("/shop-the-project/order", buildPayload());
    if (data.quotation) throw new Error("quotation");
    if (data.free && data.checkout_url) { window.location.href = data.checkout_url; throw new Error("covered-by-giftcard"); }
    return data.order_id;
  };

  const chip = (active) =>
    `px-4 py-2.5 min-h-[44px] text-xs tracking-[0.08em] border transition-colors ${active ? "bg-ink text-bone border-ink" : "border-hair bg-white hover:border-ink"}`;

  return (
    <div data-testid="page-shop-the-project">
      {/* HERO */}
      <section className="bg-ink text-bone overflow-hidden">
        <div className="container-x pt-12 pb-20 md:pt-20 md:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-7">
            <span className="eyebrow !text-gold">Shop the Project</span>
            <h1 className="font-serif text-5xl md:text-7xl leading-[1.04] mt-6">
              Dal progetto agli acquisti, <em className="text-gold font-light">senza lasciare nulla al caso.</em>
            </h1>
            <p className="mt-8 text-bone/70 text-lg font-light leading-relaxed max-w-xl">
              Una selezione personalizzata di arredi, illuminazione e complementi curata dallo Studio,
              raccolta in un'elegante Shopping List pronta da acquistare.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button onClick={() => document.getElementById("come-funziona")?.scrollIntoView({ behavior: "smooth" })} className="inline-flex items-center gap-2 border border-bone/50 px-8 py-3.5 min-h-[48px] text-xs tracking-[0.16em] uppercase hover:bg-bone hover:text-ink transition-colors" data-testid="stp-cta-how">
                Scopri come funziona <ArrowDown size={14} />
              </button>
              <button onClick={scrollToForm} className="inline-flex items-center gap-2 bg-gold text-ink px-8 py-3.5 min-h-[48px] text-xs tracking-[0.16em] uppercase hover:bg-bone transition-colors" data-testid="stp-cta-request-top">
                Inizia il tuo Shop the Project
              </button>
            </div>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="relative">
              <div className="absolute -top-4 -right-4 w-full h-full border border-gold/30" aria-hidden />
              <div className="relative overflow-hidden">
                <FocalImage
                  settings={settings}
                  prefix="stp_page"
                  fallback="https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?w=1600&q=85&auto=format"
                  alt="Ambiente curato dallo Studio con arredi selezionati"
                  loading="eager"
                  className="w-full h-[320px] md:h-[460px] object-cover"
                />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* POSIZIONAMENTO */}
      <section className="container-x py-16 md:py-24" data-testid="stp-positioning">
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow text-gold">Per chi è</span>
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.1] mt-4">Hai già definito gli spazi ma non sai cosa acquistare?</h2>
            <p className="mt-6 text-taupe leading-relaxed font-light">
              Shop the Project è il servizio di selezione arredi pensato per chi non necessita di una progettazione completa.
              Lo Studio seleziona arredi, illuminazione e complementi coordinati in base allo stile, alle dimensioni
              dell'ambiente e al budget, raccogliendoli in un'elegante Shopping List personalizzata pronta da acquistare.
            </p>
          </div>
        </Reveal>
      </section>

      {/* COME FUNZIONA */}
      <section id="come-funziona" className="bg-white border-y border-hair py-20 md:py-28" data-testid="stp-how">
        <div className="container-x">
          <Reveal>
            <span className="eyebrow text-gold">Come funziona</span>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.08] mt-4 max-w-2xl">Quattro passaggi, un solo interlocutore.</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {STEPS_INFO.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="bg-bone border border-hair p-6 md:p-8 h-full flex flex-col" data-testid={`stp-step-${i + 1}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-serif text-3xl text-gold">{s.n}</span>
                    <s.icon size={22} className="text-taupe" />
                  </div>
                  <h3 className="font-serif text-xl mt-6">{s.title}</h3>
                  <p className="text-sm text-taupe mt-3 leading-relaxed">{s.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LA TUA SHOPPING LIST — mockup editoriale */}
      <section className="container-x py-20 md:py-28 overflow-hidden" data-testid="stp-mockup">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5">
            <span className="eyebrow text-gold">La tua Shopping List</span>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.08] mt-4">Non un listino. Un piccolo catalogo, solo tuo.</h2>
            <p className="mt-6 text-taupe leading-relaxed font-light">
              Il cliente riceve una selezione professionale organizzata ambiente per ambiente e immediatamente
              acquistabile: ogni scheda riporta fotografia, brand, caratteristiche, finiture, quantità,
              disponibilità, tempi indicativi e il link diretto all'acquisto.
            </p>
            <ul className="mt-6 space-y-3">
              {["Copertina personalizzata con il nome del tuo progetto", "Prezzi di listino e condizioni a te riservate", "Note dello Studio su accostamenti e finiture", "Pulsante Acquista su ogni prodotto"].map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-ink/80">
                  <Check size={15} className="text-gold shrink-0 mt-0.5" />{t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-7">
            <div className="relative mx-auto max-w-md" data-testid="stp-pdf-mockup">
              <div className="absolute inset-0 translate-x-4 translate-y-4 bg-hair" aria-hidden />
              <div className="absolute inset-0 translate-x-2 translate-y-2 bg-bone border border-hair" aria-hidden />
              <div className="relative bg-white border border-hair shadow-xl">
                <div className="bg-ink text-bone p-6">
                  <div className="text-[8px] tracking-[0.3em] uppercase text-gold">Shop the Project</div>
                  <div className="font-serif text-xl mt-3">Casa Aurora — Soggiorno</div>
                  <div className="text-[10px] text-bone/60 mt-1">Curated by Giovanna Matino Interior</div>
                </div>
                <div className="p-5 space-y-4">
                  {[
                    { brand: "FLOS", name: "Lampada Arco", price: "€ 2.450,00", reserved: "€ 2.205,00" },
                    { brand: "CASSINA", name: "Divano Maralunga", price: "€ 5.890,00", reserved: "€ 5.301,00" },
                    { brand: "GAN", name: "Tappeto in lana", price: "€ 890,00", reserved: "€ 801,00" },
                  ].map((r) => (
                    <div key={r.name} className="flex gap-3 items-center border-b border-hair pb-4 last:border-0">
                      <div className="w-12 h-12 bg-bone border border-hair shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[8px] tracking-[0.2em] text-gold uppercase">{r.brand}</div>
                        <div className="font-serif text-sm">{r.name}</div>
                        <div className="text-[10px] text-taupe line-through inline">{r.price}</div>
                        <div className="text-[10px] text-gold font-medium inline ml-2">{r.reserved}</div>
                      </div>
                      <span className="text-[9px] tracking-[0.14em] uppercase text-gold shrink-0">Acquista →</span>
                    </div>
                  ))}
                </div>
                <div className="bg-bone border-t border-hair p-4 flex justify-between items-center">
                  <span className="text-[9px] tracking-[0.16em] uppercase text-taupe">Totale Shop the Project</span>
                  <span className="font-serif text-lg">€ 8.307,00</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COSA RICEVI / COSA NON COMPRENDE */}
      <section className="bg-white border-y border-hair py-20 md:py-28" data-testid="stp-scope">
        <div className="container-x grid lg:grid-cols-2 gap-12 lg:gap-16">
          <Reveal>
            <span className="eyebrow text-gold">Cosa ricevi</span>
            <ul className="mt-8 space-y-3.5">
              {COMPRENDE.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-ink/85 leading-relaxed" data-testid="stp-includes-item">
                  <Check size={15} className="text-gold shrink-0 mt-0.5" />{t}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.1}>
            <span className="eyebrow text-taupe">Cosa non comprende</span>
            <p className="mt-6 text-sm text-taupe leading-relaxed">Shop the Project non comprende:</p>
            <ul className="mt-4 space-y-2.5">
              {NON_COMPRENDE.map((t) => (
                <li key={t} className="flex items-start gap-3 text-sm text-taupe leading-relaxed" data-testid="stp-excludes-item">
                  <X size={14} className="text-taupe/70 shrink-0 mt-0.5" />{t}
                </li>
              ))}
            </ul>
            <p className="mt-6 text-sm text-taupe leading-relaxed">Per queste esigenze ci sono i servizi di progettazione dello Studio.</p>
            <Link to="/servizi" className="mt-5 inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase border-b border-gold text-ink hover:text-gold transition-colors pb-1" data-testid="stp-to-services-scope">
              Scopri i servizi di progettazione <ArrowUpRight size={13} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* PREZZI — tariffa professionale per superficie */}
      <section className="container-x py-20 md:py-28" data-testid="stp-pricing">
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow text-gold">La tariffa del servizio</span>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.08] mt-4">Un prezzo chiaro, in base alla superficie.</h2>
            <p className="mt-6 text-taupe leading-relaxed font-light">
              Il compenso è determinato dalla superficie complessiva interessata dalla selezione.
              Nessun costo nascosto: la Shopping List e la revisione sono incluse.
            </p>
          </div>
        </Reveal>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4 mt-12">
          {TIERS.map((t, i) => (
            <Reveal key={t.key} delay={i * 0.06}>
              <button
                onClick={() => { setTier(t.key); setStep(0); scrollToForm(); }}
                className="w-full text-left bg-white border border-hair p-6 h-full hover:border-gold transition-colors group"
                data-testid={`stp-price-${t.key}`}
              >
                <div className="text-[10px] tracking-[0.14em] uppercase text-taupe">{t.label}</div>
                <div className="font-serif text-3xl mt-3 group-hover:text-gold transition-colors">
                  {t.price ? eur(t.price) : <span className="text-xl">Su richiesta</span>}
                </div>
                <div className="mt-4 text-[10px] tracking-[0.14em] uppercase text-gold inline-flex items-center gap-1.5">
                  {t.price ? "Inizia" : "Richiedi quotazione"} <ArrowUpRight size={12} />
                </div>
              </button>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <div className="mt-10 max-w-3xl text-[11px] text-taupe leading-relaxed space-y-2 border-l-2 border-gold/40 pl-4" data-testid="stp-conditions">
            <p>La Shopping List comprende la selezione degli elementi necessari a completare gli ambienti oggetto dell'incarico, secondo la valutazione progettuale dello Studio.</p>
            <p>È inclusa una revisione della Shopping List finalizzata alla sostituzione di alcuni degli articoli proposti. La revisione non comprende la realizzazione di una nuova selezione completa o la modifica sostanziale del brief iniziale. Eventuali revisioni ulteriori potranno essere quotate separatamente.</p>
            <p>La selezione viene effettuata prioritariamente tra prodotti disponibili attraverso lo Shop e i partner selezionati da Giovanna Matino Interior.</p>
            <p>Shop the Project è un servizio di selezione degli acquisti per ambienti già definiti: non comprende layout, progettazione distributiva, render, elaborati tecnici, progettazione impianti, arredi su misura né direzione lavori. Per la progettazione dell'ambiente scopri Essenza e gli altri servizi dello Studio.</p>
          </div>
        </Reveal>
      </section>

      {/* VANTAGGIO CLIENTE STUDIO */}
      <section id="accesso" className="bg-white border-y border-hair py-20 md:py-28" data-testid="stp-benefit">
        <Reveal>
          <div className="max-w-3xl mx-auto text-center">
            <span className="eyebrow text-gold">Il progetto continua nello Shop</span>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.08] mt-4">Chi progetta con noi, acquista meglio.</h2>
            <p className="mt-6 text-taupe leading-relaxed font-light">
              I clienti Shop the Project accedono a condizioni esclusive riservate sui prodotti idonei dello Shop.
            </p>
            <div className="font-serif text-6xl text-gold mt-6" data-testid="stp-benefit-percent">−{percent}%</div>
            <p className="mt-4 text-taupe leading-relaxed font-light">
              Un vantaggio riservato ai clienti dello Studio, applicato automaticamente ai prodotti idonei — senza coupon.
            </p>
            <button onClick={openAccess} className="mt-8 inline-flex items-center gap-2 border border-gold/40 text-gold px-5 py-3 min-h-[44px] text-[11px] tracking-[0.14em] uppercase hover:bg-gold hover:text-ink transition-colors" data-testid="stp-access-btn">
              <Lock size={13} /> Accesso personale e riservato
            </button>
            <p className="mt-3 text-[11px] text-taupe">Hai già un progetto con lo Studio? Accedi alla tua area con il link personale ricevuto via email.</p>
          </div>
        </Reveal>
      </section>

      {/* DIFFERENZA CON LA PROGETTAZIONE */}
      <section className="container-x py-20 md:py-24" data-testid="stp-vs-design">
        <Reveal>
          <div className="max-w-3xl">
            <span className="eyebrow text-gold">Hai bisogno anche di progettare lo spazio?</span>
            <p className="mt-6 text-taupe leading-relaxed font-light text-lg">
              Shop the Project è ideale quando gli ambienti sono già definiti e hai bisogno di scegliere cosa acquistare.
              Se invece devi ripensare distribuzione, funzionalità, materiali, illuminazione o immagine complessiva
              dell'ambiente, scopri i servizi di progettazione Giovanna Matino Interior.
            </p>
            <Link to="/servizi" className="mt-6 inline-flex items-center gap-2 bg-ink text-bone px-8 py-3.5 min-h-[48px] text-xs tracking-[0.16em] uppercase hover:bg-gold hover:text-ink transition-colors" data-testid="stp-to-essenza">
              Scopri Essenza e gli altri servizi <ArrowUpRight size={14} />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* CONFIGURATORE */}
      <section id="richiedi" ref={formRef} className="bg-ink text-bone py-20 md:py-28" data-testid="stp-form-section">
        <div className="container-x max-w-4xl">
          <Reveal>
            <span className="eyebrow !text-gold">Inizia ora</span>
            <h2 className="font-serif text-4xl md:text-5xl leading-[1.08] mt-4">Il tuo Shop the Project, in pochi passaggi.</h2>
          </Reveal>

          {done ? (
            <div className="mt-12 bg-bone text-ink p-10 text-center" data-testid="stp-success">
              <div className="mx-auto w-12 h-12 border border-gold text-gold flex items-center justify-center"><Check size={22} /></div>
              <h3 className="font-serif text-3xl mt-5">{done.quotation ? "Richiesta di quotazione inviata." : "Richiesta ricevuta."}</h3>
              <p className="text-taupe mt-3 text-sm leading-relaxed max-w-md mx-auto">
                {done.quotation
                  ? "Per superfici oltre 110 m² lo Studio prepara una quotazione personalizzata: ti ricontatteremo a breve. Ti abbiamo inviato una conferma via email."
                  : "Lo Studio ti ricontatterà per definire la selezione. Ti abbiamo inviato una conferma via email."}
              </p>
              <Link to="/library" className="btn-outline-ink mt-8 inline-flex">Intanto, sfoglia le guide gratuite</Link>
            </div>
          ) : (
            <div className="mt-10" data-testid="stp-wizard">
              {/* Stepper */}
              <div className="flex gap-1.5 overflow-x-auto pb-2" data-testid="stp-stepper">
                {WIZARD_STEPS.map((s, i) => (
                  <button key={s} type="button" onClick={() => i < step && setStep(i)}
                    className={`shrink-0 px-3 py-2 text-[10px] tracking-[0.12em] uppercase border transition-colors ${i === step ? "bg-gold text-ink border-gold" : i < step ? "border-bone/40 text-bone/70 hover:border-bone" : "border-bone/15 text-bone/40 cursor-default"}`}
                    data-testid={`stp-step-tab-${i}`}>
                    {i + 1} · {s}
                  </button>
                ))}
              </div>

              <div className="mt-8 bg-bone text-ink p-6 md:p-10" data-testid={`stp-wizard-step-${step}`}>
                {/* STEP 0 — SUPERFICIE */}
                {step === 0 && (
                  <div>
                    <h3 className="font-serif text-2xl">Qual è la superficie complessiva degli ambienti che vuoi completare?</h3>
                    <div className="grid sm:grid-cols-2 gap-3 mt-6">
                      {TIERS.map((t) => (
                        <button key={t.key} type="button" onClick={() => setTier(t.key)} className={`text-left p-5 border transition-colors ${tier === t.key ? "border-gold bg-gold/5" : "border-hair bg-white hover:border-ink"}`} data-testid={`stp-tier-${t.key}`}>
                          <div className="text-sm">{t.label}</div>
                          <div className="font-serif text-2xl mt-2">{t.price ? eur(t.price) : "Su richiesta"}</div>
                        </button>
                      ))}
                    </div>
                    {tier && !isQuote && <p className="mt-4 text-sm text-gold" data-testid="stp-tier-price-note">Servizio: {eur(selTier.price)} — pagamento online al termine del percorso.</p>}
                    {isQuote && <p className="mt-4 text-sm text-taupe" data-testid="stp-tier-quote-note">Per superfici oltre 110 m² riceverai una quotazione personalizzata: nessun pagamento ora.</p>}
                  </div>
                )}

                {/* STEP 1 — AMBIENTI */}
                {step === 1 && (
                  <div>
                    <h3 className="font-serif text-2xl">Quali ambienti vuoi completare?</h3>
                    <p className="text-sm text-taupe mt-2">Puoi selezionarne più di uno.</p>
                    <div className="flex flex-wrap gap-2 mt-6">
                      {ENVIRONMENTS.map((v) => (
                        <button key={v} type="button" onClick={() => toggleEnv(v)} className={chip(environments.includes(v))} data-testid={`stp-env-${v.toLowerCase().replace(/\s+/g, "-")}`}>{v}</button>
                      ))}
                    </div>
                  </div>
                )}

                {/* STEP 2 — BUDGET */}
                {step === 2 && (
                  <div>
                    <h3 className="font-serif text-2xl">Qual è il budget indicativo destinato agli acquisti?</h3>
                    <p className="text-sm text-taupe mt-2">È il budget destinato ai prodotti, non il prezzo del servizio Shop the Project.</p>
                    <div className="flex flex-wrap gap-2 mt-6">
                      {BUDGETS.map((v) => (
                        <button key={v} type="button" onClick={() => setBudget(v)} className={chip(budget === v)} data-testid={`stp-budget-${BUDGETS.indexOf(v)}`}>{v}</button>
                      ))}
                    </div>
                  </div>
                )}

                {/* STEP 3 — TEMPISTICA */}
                {step === 3 && (
                  <div>
                    <h3 className="font-serif text-2xl">Quando prevedi di effettuare gli acquisti?</h3>
                    <div className="flex flex-wrap gap-2 mt-6">
                      {TIMELINES.map((v) => (
                        <button key={v} type="button" onClick={() => setTimeline(v)} className={chip(timeline === v)} data-testid={`stp-timeline-${TIMELINES.indexOf(v)}`}>{v}</button>
                      ))}
                    </div>
                  </div>
                )}

                {/* STEP 4 — BRIEF */}
                {step === 4 && (
                  <div className="space-y-4">
                    <h3 className="font-serif text-2xl">Il tuo brief</h3>
                    <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Stile e preferenze</Label>
                      <Textarea rows={2} value={brief.style} onChange={(e) => setBrief({ ...brief, style: e.target.value })} placeholder="es. moderno caldo, nordico, classico contemporaneo…" className="rounded-none mt-1" data-testid="stp-brief-style" /></div>
                    <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Esigenze</Label>
                      <Textarea rows={2} value={brief.needs} onChange={(e) => setBrief({ ...brief, needs: e.target.value })} placeholder="es. divano comodo per la sera, luce diffusa in zona lettura…" className="rounded-none mt-1" data-testid="stp-brief-needs" /></div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Colori / materiali desiderati</Label>
                        <Input value={brief.colors} onChange={(e) => setBrief({ ...brief, colors: e.target.value })} className="rounded-none mt-1" data-testid="stp-brief-colors" /></div>
                      <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Prodotti già presenti da mantenere</Label>
                        <Input value={brief.keep_existing} onChange={(e) => setBrief({ ...brief, keep_existing: e.target.value })} className="rounded-none mt-1" data-testid="stp-brief-keep" /></div>
                    </div>
                    <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Note</Label>
                      <Textarea rows={2} value={brief.notes} onChange={(e) => setBrief({ ...brief, notes: e.target.value })} className="rounded-none mt-1" data-testid="stp-brief-notes" /></div>
                    <div>
                      <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Allegati (foto dell'ambiente, planimetria, ispirazioni — max 5)</Label>
                      <div className="mt-2 flex items-center gap-3 flex-wrap">
                        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 border border-ink hover:bg-ink hover:text-bone transition-colors disabled:opacity-50" data-testid="stp-upload-btn">
                          {uploading ? <Loader2 size={14} className="animate-spin" /> : <Paperclip size={14} />} Carica file
                        </button>
                        <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={uploadFile} data-testid="stp-upload-input" />
                        {attachments.map((a, i) => (
                          <span key={i} className="text-xs bg-white border border-hair px-3 py-1.5 inline-flex items-center gap-2" data-testid={`stp-attachment-${i}`}>
                            {a.name.length > 24 ? a.name.slice(0, 24) + "…" : a.name}
                            <button type="button" onClick={() => setAttachments((p) => p.filter((_, j) => j !== i))} className="text-taupe hover:text-red-700" aria-label="Rimuovi"><X size={12} /></button>
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* STEP 5 — DATI CLIENTE */}
                {step === 5 && (
                  <div className="space-y-4">
                    <h3 className="font-serif text-2xl">I tuoi dati</h3>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Nome *</Label>
                        <Input value={customer.name} onChange={(e) => setCustomer({ ...customer, name: e.target.value })} className="rounded-none mt-1" data-testid="stp-name" /></div>
                      <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Cognome *</Label>
                        <Input value={customer.surname} onChange={(e) => setCustomer({ ...customer, surname: e.target.value })} className="rounded-none mt-1" data-testid="stp-surname" /></div>
                      <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Email *</Label>
                        <Input type="email" value={customer.email} onChange={(e) => setCustomer({ ...customer, email: e.target.value })} className="rounded-none mt-1" data-testid="stp-email" /></div>
                      <div><Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Telefono</Label>
                        <Input value={customer.phone} onChange={(e) => setCustomer({ ...customer, phone: e.target.value })} className="rounded-none mt-1" data-testid="stp-phone" /></div>
                    </div>
                    <div aria-hidden="true" style={{ position: "absolute", left: "-9999px" }}>
                      <input type="text" tabIndex={-1} autoComplete="off" value={website} onChange={(e) => setWebsite(e.target.value)} data-testid="stp-website" />
                    </div>
                    <label className="flex items-start gap-3 text-xs text-taupe leading-relaxed cursor-pointer min-h-[44px]">
                      <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="w-5 h-5 mt-0.5 shrink-0 accent-[#9E8462]" data-testid="stp-privacy" />
                      <span>Ho letto l'<Link to="/privacy" target="_blank" className="underline hover:text-ink">informativa privacy</Link> e acconsento al trattamento dei dati, necessario all'erogazione del servizio. *</span>
                    </label>
                    <label className="flex items-start gap-3 text-xs text-taupe leading-relaxed cursor-pointer min-h-[44px]">
                      <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="w-5 h-5 mt-0.5 shrink-0 accent-[#9E8462]" data-testid="stp-marketing" />
                      <span>Desidero ricevere guide, consigli, ispirazioni e aggiornamenti da Giovanna Matino Interior. (facoltativo)</span>
                    </label>
                  </div>
                )}

                {/* STEP 6 — RIEPILOGO + PAGAMENTO */}
                {step === 6 && selTier && (
                  <div data-testid="stp-summary">
                    <h3 className="font-serif text-2xl">Riepilogo</h3>
                    <dl className="mt-6 space-y-3 text-sm">
                      <div className="flex justify-between border-b border-hair pb-3"><dt className="text-taupe">Servizio</dt><dd className="font-medium text-right">Shop the Project — {selTier.label.toLowerCase()}</dd></div>
                      <div className="flex justify-between border-b border-hair pb-3"><dt className="text-taupe">Ambienti</dt><dd className="text-right">{environments.join(", ")}</dd></div>
                      <div className="flex justify-between border-b border-hair pb-3"><dt className="text-taupe">Budget prodotti</dt><dd>{budget}</dd></div>
                      <div className="flex justify-between border-b border-hair pb-3"><dt className="text-taupe">Tempistica</dt><dd>{timeline}</dd></div>
                      {attachments.length > 0 && <div className="flex justify-between border-b border-hair pb-3"><dt className="text-taupe">Allegati</dt><dd>{attachments.length} file</dd></div>}
                      <div className="flex justify-between border-b border-hair pb-3"><dt className="text-taupe">Cliente</dt><dd className="text-right">{customer.name} {customer.surname} · {customer.email}</dd></div>
                      <div className="flex justify-between items-baseline pt-2">
                        <dt className="eyebrow">Prezzo del servizio</dt>
                        <dd className="font-serif text-3xl" data-testid="stp-summary-price">{isQuote ? "Su richiesta" : eur(selTier.price)}</dd>
                      </div>
                    </dl>
                    <p className="text-[11px] text-taupe mt-3 leading-relaxed">
                      Il vantaggio Cliente Studio (−{percent}%) riguarda esclusivamente i prodotti idonei dello Shop acquistati successivamente: non si applica al costo del servizio.
                    </p>
                    {isQuote ? (
                      <button onClick={submit} disabled={busy} className="btn-ink w-full min-h-[48px] mt-6 disabled:opacity-40 inline-flex items-center justify-center gap-2" data-testid="stp-submit-quote">
                        {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                        {busy ? "Invio in corso…" : "Richiedi quotazione personalizzata"}
                      </button>
                    ) : (
                      <div className="mt-6 space-y-4">
                        <div className="flex gap-2">
                          <button type="button" onClick={() => setMethod("stripe")} className={chip(method === "stripe") + " flex-1"} data-testid="stp-method-stripe">Carta · Stripe</button>
                          <button type="button" onClick={() => setMethod("paypal")} disabled={!paypalCfg?.enabled} className={chip(method === "paypal") + " flex-1 disabled:opacity-40"} data-testid="stp-method-paypal">PayPal</button>
                        </div>
                        {method === "stripe" ? (
                          <div>
                            <div className="mb-4" data-testid="stp-giftcard">
                              <div className="text-[10px] tracking-[0.14em] uppercase text-taupe mb-2">Hai una Gift Card?</div>
                              <div className="flex gap-2">
                                <Input value={gcCode} onChange={(e) => { setGcCode(e.target.value.toUpperCase()); setGc(null); }} placeholder="GMI-XXXX-XXXX-XXXX" className="rounded-none font-mono text-base" data-testid="stp-giftcard-input" />
                                <button type="button" disabled={gcBusy || !gcCode.trim()} onClick={async () => {
                                  setGcBusy(true);
                                  try {
                                    const { data } = await api.post("/gift-cards/validate", { code: gcCode.trim() });
                                    if (data.valid) { setGc(data); toast.success(`Gift Card applicata: credito ${eur(data.balance)}`); }
                                    else { setGc(null); toast.error(data.error || "Codice non valido"); }
                                  } catch { setGc(null); toast.error("Verifica non riuscita"); }
                                  finally { setGcBusy(false); }
                                }} className="min-h-[44px] px-4 border border-ink text-xs tracking-[0.12em] uppercase hover:bg-ink hover:text-bone transition-colors disabled:opacity-40 shrink-0" data-testid="stp-giftcard-apply">
                                  {gcBusy ? "…" : "Applica"}
                                </button>
                              </div>
                              {gc && <p className="text-[11px] text-gold mt-2" data-testid="stp-giftcard-note">Credito applicato: −{eur(Math.min(gc.balance, selTier.price))} · da pagare: {eur(Math.max(0, selTier.price - Math.min(gc.balance, selTier.price)))}</p>}
                            </div>
                            <button onClick={submit} disabled={busy} className="btn-ink w-full min-h-[48px] disabled:opacity-40 inline-flex items-center justify-center gap-2" data-testid="stp-submit-pay">
                              {busy ? <Loader2 size={15} className="animate-spin" /> : <Lock size={14} />}
                              {busy ? "Reindirizzamento…" : (gc && gc.balance >= selTier.price ? "Conferma (coperto dalla Gift Card)" : `Procedi al pagamento · ${eur(Math.max(0, selTier.price - (gc ? Math.min(gc.balance, selTier.price) : 0)))}`)}
                            </button>
                          </div>
                        ) : (
                          <div data-testid="stp-paypal-buttons">
                            {paypalCfg?.client_id && (
                              <PayPalScriptProvider options={{ clientId: paypalCfg.client_id, currency: "EUR", locale: "it_IT", intent: "capture" }}>
                                <PayPalButtons
                                  style={{ layout: "vertical", shape: "rect", label: "pay" }}
                                  forceReRender={[selTier.price]}
                                  createOrder={startPaypal}
                                  onApprove={async (data) => {
                                    try {
                                      await api.post(`/payments/paypal/capture/${data.orderID}`);
                                      navigate(`/ordine/conferma?session_id=${data.orderID}`);
                                    } catch (err) { toast.error(err.response?.data?.detail || "Verifica pagamento non riuscita"); }
                                  }}
                                  onError={() => toast.error("PayPal non disponibile. Riprova o paga con carta.")}
                                />
                              </PayPalScriptProvider>
                            )}
                          </div>
                        )}
                        <p className="text-[11px] text-taupe text-center">Pagamento sicuro. Il servizio si attiva alla conferma del pagamento.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Navigazione wizard */}
                <div className="flex justify-between mt-8 pt-6 border-t border-hair">
                  <button type="button" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0}
                    className="min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 border border-hair hover:border-ink transition-colors disabled:opacity-30" data-testid="stp-prev">
                    <ArrowLeft size={14} /> Indietro
                  </button>
                  {step < 6 && (
                    <button type="button" onClick={() => canNext() && setStep((s) => s + 1)} disabled={!canNext()}
                      className="min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-6 py-2 bg-ink text-bone hover:bg-gold hover:text-ink transition-colors disabled:opacity-40" data-testid="stp-next">
                      Avanti <ArrowRight size={14} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Modal accesso personale */}
      {accessOpen && (
        <div className="fixed inset-0 z-[60] bg-ink/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setAccessOpen(false)}>
          <div className="relative max-w-md w-full bg-bone p-8 md:p-10" onClick={(e) => e.stopPropagation()} data-testid="stp-access-modal">
            <button onClick={() => setAccessOpen(false)} className="absolute top-4 right-4 p-2 min-w-[44px] min-h-[44px] hover:bg-gold hover:text-ink transition-colors" data-testid="stp-access-close" aria-label="Chiudi"><X size={16} /></button>
            <div className="eyebrow text-gold">Area clienti Studio</div>
            <h3 className="font-serif text-3xl mt-2">Il tuo accesso personale.</h3>
            <p className="text-sm text-taupe mt-3 leading-relaxed">
              Incolla il link personale che hai ricevuto via email o nella tua Shopping List:
              vedrai la tua selezione con le condizioni a te riservate già applicate.
            </p>
            <form onSubmit={submitAccess} className="mt-6 space-y-4">
              <div>
                <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Link personale</Label>
                <Input value={accessLink} onChange={(e) => setAccessLink(e.target.value)} placeholder="https://…/progetto/…" className="rounded-none mt-1 font-mono text-xs" data-testid="stp-access-input" />
              </div>
              {accessError && <p className="text-xs text-red-700 leading-relaxed" data-testid="stp-access-error">{accessError}</p>}
              <button type="submit" disabled={accessBusy || !accessLink.trim()} className="btn-ink w-full min-h-[48px] disabled:opacity-40 inline-flex items-center justify-center gap-2" data-testid="stp-access-submit">
                {accessBusy ? <Loader2 size={15} className="animate-spin" /> : <Lock size={14} />}
                {accessBusy ? "Verifica in corso…" : "Accedi alla tua area"}
              </button>
            </form>
            <p className="text-[11px] text-taupe mt-4 leading-relaxed">
              Non hai ancora un accesso? Viene creato dallo Studio insieme alla tua Shopping List.{" "}
              <button type="button" onClick={() => { setAccessOpen(false); scrollToForm(); }} className="underline hover:text-ink" data-testid="stp-access-to-form">Inizia il tuo Shop the Project</button>
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
